import cn from "classnames";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DoubleLeft, DoubleRight, Left, Right } from "../../utils/icons";
import { DAYS, MONTHS } from "./stubs";
import useDatePicker from "./useDatePicker";

export default function DatePicker({
  label = "Date",
  date = null,
  onChange = () => {},
  placeholder = "Select a date",
}) {
  const {
    month = "",
    year = "",
    dates = [],
    selectedDate = "",
    showPicker = false,
    gotoNextYear = () => {},
    gotoPrevYear = () => {},
    gotoNextMonth = () => {},
    gotoPrevMonth = () => {},
    setShowPicker = () => {},
  } = useDatePicker(date);

  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const inputRef = useRef();
  const calendarRef = useRef();

  const closePicker = useCallback(() => setShowPicker(false), [setShowPicker]);

  const updatePickerPosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setPickerPosition({
      top: rect.bottom + 6,
      left: rect.left,
    });
  }, []);

  useLayoutEffect(() => {
    if (showPicker) {
      updatePickerPosition();
    }
  }, [showPicker, updatePickerPosition]);

  useEffect(() => {
    if (!showPicker) return;
    updatePickerPosition();
    window.addEventListener("resize", updatePickerPosition);
    window.addEventListener("scroll", updatePickerPosition, true);
    return () => {
      window.removeEventListener("resize", updatePickerPosition);
      window.removeEventListener("scroll", updatePickerPosition, true);
    };
  }, [showPicker, updatePickerPosition]);

  useEffect(() => {
    if (!showPicker) return;

    const handleClickOutside = (e) => {
      if (
        inputRef.current?.contains(e.target) ||
        calendarRef.current?.contains(e.target)
      ) {
        return;
      }
      closePicker();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPicker, closePicker]);

  const togglePicker = (e) => {
    e.stopPropagation();
    setShowPicker((prev) => !prev);
  };

  const calendar = (
    <div
      ref={calendarRef}
      onClick={(e) => e.stopPropagation()}
      className="date-picker date-picker--portal"
      style={{
        position: "fixed",
        top: pickerPosition.top,
        left: pickerPosition.left,
        zIndex: 99999,
      }}
    >
      <div className="date-picker__header">
        <div className="date-picker__header--col">
          <DoubleLeft onClick={gotoPrevYear} />
          <Left onClick={gotoPrevMonth} />
        </div>
        <div className="date-picker__header--col">
          <span className="date-picker__month">
            {Number.isInteger(month) && MONTHS[month].abbr}
          </span>
          <span className="date-picker__year">{year}</span>
        </div>
        <div className="date-picker__header--col">
          <Right onClick={gotoNextMonth} />
          <DoubleRight onClick={gotoNextYear} />
        </div>
      </div>
      <div className="date-picker__content">
        <ul className="date-picker__days">
          {DAYS.map(({ abbr }) => (
            <li key={abbr} className="date-picker__day">
              {abbr}
            </li>
          ))}
        </ul>
        <ul className="date-picker__dates">
          {dates.map((week, index) => (
            <li key={index} className="date-picker__week">
              <ul className="date-picker__week--list">
                {week.map(({ label, value, isOfCurrentMonth, isToday }) => {
                  const isSelected =
                    date &&
                    date instanceof Date &&
                    date.getDate() === value.getDate() &&
                    date.getMonth() === value.getMonth() &&
                    date.getFullYear() === value.getFullYear();
                  return (
                    <li
                      key={value.toString()}
                      className={cn("date-picker__date", {
                        muted: !isOfCurrentMonth,
                        today: isToday,
                        selected: isSelected,
                      })}
                    >
                      <span
                        onClick={() => {
                          onChange(value);
                          closePicker();
                        }}
                      >
                        {label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      </div>
      <div className="date-picker__footer">
        <span
          className="date-picker__footer--today"
          onClick={() => {
            onChange(new Date());
            closePicker();
          }}
        >
          Today
        </span>
      </div>
    </div>
  );

  return (
    <>
      <div className="date-picker__trigger">
        <input
          ref={inputRef}
          value={selectedDate}
          type="text"
          readOnly
          onClick={togglePicker}
          placeholder={placeholder}
          className="datepicker hasDatepicker"
        />
        <i className="far fa-calendar-alt"></i>
      </div>
      {showPicker && createPortal(calendar, document.body)}
    </>
  );
}
